import React, { useState, useEffect, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { fetchMagazineBySlug, fetchMagazineText } from "../../lib/api";
import { formatDateUTC } from "../../lib/date";
import FlipbookViewer from "../../components/Flipbook/FlipbookViewer";

export default function FlipbookReader() {
  const { slug } = useParams();
  const [mag, setMag] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [textData, setTextData] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        setErr(null);
        const { item } = await fetchMagazineBySlug(slug);
        if (!alive) return;
        setMag(item || null);
        
        // Fetch text data for search if available
        if (item && item.webpKeys?.length > 0 && item.textDataKey) {
          try {
            const result = await fetchMagazineText(slug);
            if (!alive) return;
            setTextData(result?.textData || null);
          } catch (textErr) {
            console.warn('Text data not available:', textErr);
          }
        }
      } catch (e) {
        if (!alive) return;
        setErr(e);
      } finally {
        if (!alive) return;
        setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [slug]);

  // Always use WebP format (no PDF fallback)
  const { viewerProps } = useMemo(() => {
    if (!mag) return { viewerProps: { pageImages: [] } };
    
    const publicUrl = mag.publicUrl?.replace(/\/$/, "") || "";
    const hasWebP = mag.webpKeys?.length > 0 && mag.formats?.webp;
    
    if (!hasWebP) {
      // No WebP available - shouldn't happen with new upload flow
      console.warn('Magazine has no WebP pages available');
      return { viewerProps: { pageImages: [] } };
    }
    
    const pageImages = mag.webpKeys.map((key, index) => ({
      page: index + 1,
      primary: {
        url: `${publicUrl}/${key}`,
        width: 1400,
        height: 1820,
      }
    }));
    
    return {
      viewerProps: {
        pageImages,
        textData,
        slug,
      }
    };
  }, [mag, textData, slug]);

  if (loading) {
    return (
      <main className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-gray-300 mb-4" style={{ borderTopColor: '#FF9843' }}></div>
          <p className="font-medium text-gray-600">Loading magazine...</p>
        </div>
      </main>
    );
  }

  if (err || !mag) {
    return (
      <main className="min-h-screen flex items-center justify-center">
        <div className="max-w-md mx-auto px-4 text-center">
          <div className="rounded-2xl shadow-xl p-8 border-2 border-gray-300">
            <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <h2 className="text-xl font-bold mb-2">Failed to Load</h2>
            <p className="mb-6 text-gray-600">We couldn't load this magazine. It may have been removed or you don't have permission to view it.</p>
            <Link 
              to="/magazines" 
              className="inline-flex items-center px-6 py-3 text-white rounded-md transition-all duration-200 font-medium hover:opacity-90 no-underline"
              style={{ backgroundColor: '#FF9843' }}
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to Magazines
            </Link>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="w-full mt-16 sm:mt-0">
      <header className="border-b border-gray-200 shadow-sm flipbook-reader-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl font-bold truncate">{mag.title || "Untitled Issue"}</h1>
              <div className="flex items-center mt-1 text-sm">
                <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                {formatDateUTC(mag.publishedAt, "MMM D, YYYY") || "Date Unknown"}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Link 
                to="/magazines" 
                className="inline-flex items-center px-4 py-2.5 text-sm font-medium text-white rounded-md hover:opacity-90 transition-all duration-200 no-underline"
                style={{ backgroundColor: '#4cc8f4' }}
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                All Issues
              </Link>
            </div>
          </div>
        </div>
      </header>

      <FlipbookViewer
        {...viewerProps}
        title={mag.title || "Untitled Issue"}
        publishDate={mag.publishedAt}
        initialPage={0}
        pageWidth={1400}
        quality={1.35}
        dprCap={2.5}
        onError={(e) => console.error("Flipbook error:", e)}
      />
    </main>
  );
}